import { t } from '@framework/i18n';
import { AlignRow } from '@framework/ui';
import { Button, Space } from 'antd';
import clx from 'classnames';
import React, { useCallback } from 'react';
import { ConfigFormItem } from '../../../../../../ui/form/ConfigFormItem';
import { IWidgetComponentProps } from '../../../../IWidgetType';
import { useWidgetEnabled } from '../../../../hooks/useWidgetEnabled';
import { IWidgetParsedProps } from '../../../inputs/common/util/getWidgetProps';
import { getIconFromType } from '../../buttons/icons/Icons';
import { ConfirmWidgetValue, IConfirmWidget } from '../IConfirmWidget';

export interface IConfirmWidgetLayoutComponentProps
  extends IWidgetComponentProps<IConfirmWidget> {
  options: IWidgetParsedProps;

  /** Valore corrente */
  confirmationValue?: ConfirmWidgetValue | null;
  /** Gestisce il click dei pulsanti per sincronizzare il valore */
  onChange?: (value: ConfirmWidgetValue) => void;
  onBlur?: () => void;
}

export function ConfirmWidgetLayoutComponent(
  props: IConfirmWidgetLayoutComponentProps,
) {
  const { widget, onChange, onBlur, options, confirmationValue } = props;

  const enabled = useWidgetEnabled(widget, options);

  const handleConfirm = useCallback(() => {
    if (confirmationValue === ConfirmWidgetValue.Unconfirmed) {
      onChange?.(ConfirmWidgetValue.ConfirmedDefault);
    } else if (confirmationValue === ConfirmWidgetValue.Editing) {
      onChange?.(ConfirmWidgetValue.ConfirmedModified);
    }
    onBlur?.();
  }, [confirmationValue, onBlur, onChange]);

  const handleSwitchEdit = useCallback(() => {
    onChange?.(
      confirmationValue === ConfirmWidgetValue.Editing
        ? ConfirmWidgetValue.Unconfirmed
        : ConfirmWidgetValue.Editing,
    );
    onBlur?.();
  }, [onChange, confirmationValue, onBlur]);

  if (props.readonly) return null;

  return (
    <ConfigFormItem
      name={options.name}
      className={clx(`align-${widget.layout?.buttonsAlign ?? 'flex-end'}`)}
      readonly={options.readonly}
    >
      <AlignRow justify={widget.layout?.buttonsAlign ?? 'flex-end'}>
        <Space>
          <Button
            size="large"
            type={widget.layout?.confirmButtonType ?? 'primary'}
            icon={getIconFromType(widget.layout?.confirmIcon)}
            disabled={
              enabled === false ||
              confirmationValue === ConfirmWidgetValue.ConfirmedDefault ||
              confirmationValue === ConfirmWidgetValue.ConfirmedModified
            }
            onClick={handleConfirm}
          >
            {confirmationValue === ConfirmWidgetValue.ConfirmedDefault ||
            confirmationValue === ConfirmWidgetValue.ConfirmedModified
              ? t(
                  'widgets.confirm-widget-layout-component.confirm',
                  'Confermato',
                )
              : widget.layout?.confirmText}
          </Button>
          <Button
            size="large"
            type={widget.layout?.editButtonType ?? 'primary'}
            icon={getIconFromType(widget.layout?.editIcon)}
            disabled={enabled === false}
            onClick={handleSwitchEdit}
          >
            {confirmationValue === ConfirmWidgetValue.Editing
              ? t(
                  'widgets.confirm-widget-layout-component.cancel',
                  'Annulla Modifiche',
                )
              : widget.layout?.editText ??
                t(
                  'widgets.confirm-widget-layout-component.edit',
                  'Modifica',
                )}
          </Button>
        </Space>
      </AlignRow>
    </ConfigFormItem>
  );
}
